import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
} from "@mui/material";
import {
  CloudUpload,
} from "@mui/icons-material";
import { processPathInventoryOCR } from "../../../services/pathOCRService";
import {
  OCR_UPLOAD_DIALOG_PAPER_SX,
  STANDARD_FORM_DIALOG_TITLE_SX,
  STANDARD_FORM_DIALOG_ACTIONS_SX,
  FORM_CANCEL_BUTTON_SX,
  FORM_SUBMIT_BUTTON_SX,
} from "../../../utils/formModalStyles";

const PathOCRDialog = ({ open, onClose, onOCRComplete }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type - support both images and PDFs
      if (!file.type.startsWith("image/") && file.type !== "application/pdf") {
        setError("Please select an image file (JPG, PNG, etc.) or PDF file");
        return;
      }
      
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        setError("File size must be less than 10MB");
        return;
      }
      
      setSelectedFile(file);
      setError("");
    }
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });
  };

  const convertPdfToImage = async (pdfFile) => {
    try {
      // Load PDF.js from CDN dynamically
      if (!window.pdfjsLib) {
        await new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
          script.onload = () => {
            // Set up the worker
            window.pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
            resolve();
          };
          script.onerror = reject;
          document.head.appendChild(script);
        });
      }
      
      const arrayBuffer = await pdfFile.arrayBuffer();
      const pdf = await window.pdfjsLib.getDocument(arrayBuffer).promise;
      
      // Get the first page
      const page = await pdf.getPage(1);
      const viewport = page.getViewport({ scale: 2.0 }); // Higher scale for better quality
      
      // Create canvas
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      canvas.height = viewport.height;
      canvas.width = viewport.width;
      
      // Render PDF page to canvas
      await page.render({
        canvasContext: context,
        viewport: viewport
      }).promise;
      
      // Convert canvas to base64 image
      return canvas.toDataURL('image/jpeg', 0.9);
    } catch (error) {
      console.error('Error converting PDF to image:', error);
      throw new Error('Failed to convert PDF to image. Please try using an image file instead.');
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError("Please select a file first");
      return;
    }

    setUploading(true);
    setError("");

    try {
      // Convert file to base64 (handle PDF conversion)
      let base64;
      let fileType = selectedFile.type;
      
      if (selectedFile.type === 'application/pdf') {
        try {
          // Convert PDF to image first
          console.log("Converting PDF to image...");
          base64 = await convertPdfToImage(selectedFile);
          fileType = 'image/jpeg'; // Converted to JPEG
          console.log("PDF conversion successful");
        } catch (pdfError) {
          console.error("PDF conversion failed:", pdfError);
          throw new Error("Failed to convert PDF to image. Please try using an image file instead or take a screenshot of the PDF.");
        }
      } else {
        // Regular image file
        base64 = await fileToBase64(selectedFile);
      }

      // Call OCR processing with the converted image
      console.log("Starting OCR processing with file type:", fileType);
      const result = await processPathInventoryOCR(base64, fileType);
      console.log("OCR processing completed:", result);
      
      // Proceed with the OCR result
      onOCRComplete(result);
      handleClose();
    } catch (error) {
      console.error("OCR processing error:", error);
      
      // Provide more specific error messages based on the error type
      let errorMessage = "Failed to process invoice. Please try again.";
      
      if (error.message.includes("503") || error.message.includes("Service Unavailable")) {
        errorMessage = "OCR service is temporarily unavailable. Please try again later.";
      } else if (error.message.includes("429") || error.message.includes("Too Many Requests")) {
        errorMessage = "Too many requests. Please wait a moment and try again.";
      } else if (error.message.includes("API key not configured")) {
        errorMessage = "OCR service is not configured. Please contact administrator.";
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      
      setError(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setError("");
    setUploading(false);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      sx={OCR_UPLOAD_DIALOG_PAPER_SX}
    >
      <DialogTitle sx={STANDARD_FORM_DIALOG_TITLE_SX}>
        Upload Pathology Inventory Invoice (Image or PDF)
      </DialogTitle>

      <DialogContent>
        <Box sx={{ p: 2 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Upload an invoice image or PDF to automatically extract path inventory items and add them to your inventory.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box
            sx={{
              border: "2px dashed #ccc",
              borderRadius: 2,
              p: 3,
              textAlign: "center",
              backgroundColor: "#fafafa",
              cursor: "pointer",
              "&:hover": {
                backgroundColor: "#f0f0f0",
              },
            }}
            onClick={() => document.getElementById("file-input").click()}
          >
            <input
              id="file-input"
              type="file"
              accept="image/*,application/pdf"
              onChange={handleFileSelect}
              style={{ display: "none" }}
              disabled={uploading}
            />
            
            {selectedFile ? (
              <Box>
                <Typography variant="h6" color="primary">
                  File Selected
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {selectedFile.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                </Typography>
              </Box>
            ) : (
              <Box>
                <CloudUpload sx={{ fontSize: 48, color: "text.secondary", mb: 1 }} />
                <Typography variant="h6" color="text.secondary">
                  Click to select invoice image or PDF
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Supported formats: JPG, PNG, GIF, WebP, PDF
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Maximum file size: 10MB
                </Typography>
              </Box>
            )}
          </Box>

          {uploading && (
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", mt: 2 }}>
              <CircularProgress size={24} sx={{ mr: 1 }} />
              <Typography variant="body2">
                Processing invoice{error && error.includes("temporarily unavailable") ? " (using sample data)" : ""}...
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={STANDARD_FORM_DIALOG_ACTIONS_SX}>
        <Button onClick={handleClose} disabled={uploading} variant="outlined" color="primary" sx={FORM_CANCEL_BUTTON_SX}>
          Cancel
        </Button>
        <Button
          onClick={handleUpload}
          variant="contained"
          color="primary"
          sx={FORM_SUBMIT_BUTTON_SX}
          disabled={!selectedFile || uploading}
        >
          {uploading ? "Processing..." : "Process Invoice"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PathOCRDialog;
